import type { JackpotLevel } from '@/api/common';
import { CurrencyCode } from '@/helpers/currency';

// ============================================================================
// Types
// ============================================================================

/** @deprecated No longer used - minBet is now stored as USD number */
export type MinBetByCurrency = Partial<Record<CurrencyCode, number>>;

export interface IHitRange {
  from: number;
  to: number;
}

export interface IStaticPool {
  title: string;
  /** Min bet amount in USD. Converted to user's currency at runtime. */
  minBet: number;
  image: string;
  type: PoolLevelEnum;
  hitRange: IHitRange;
}

export interface IPool extends IStaticPool {
  prize: number;
}

/** @deprecated Use IPool instead */
export type Pool = IPool;

// ============================================================================
// Enums
// ============================================================================

export enum PoolLevelEnum {
  PIONEERS = 'level_3',
  LEGENDS = 'level_2',
  TITANS = 'level_1',
}

export enum PoolStatusEnum {
  IN_HIT_ZONE = 'inHitZone',
  WIN_ANY_MOMENT = 'winAnyMoment',
  ABOUT_TO_BLOW = 'aboutToBlow',
}

// ============================================================================
// Constants
// ============================================================================

export const LEVELS_CONFIG: Record<
  PoolLevelEnum,
  { title: string; icon: string }
> = {
  [PoolLevelEnum.PIONEERS]: {
    title: 'shortTypes.pioneers',
    icon: '/jackpot/levels/pioneersIcon.png',
  },
  [PoolLevelEnum.LEGENDS]: {
    title: 'shortTypes.legends',
    icon: '/jackpot/levels/legendsIcon.png',
  },
  [PoolLevelEnum.TITANS]: {
    title: 'shortTypes.titans',
    icon: '/jackpot/levels/titansIcon.png',
  },
};

/**
 * Prize thresholds (in USD) for pool status badges.
 * Order matters: check from highest to lowest.
 */
export const POOL_STATUS_THRESHOLDS: Record<
  PoolLevelEnum,
  Partial<Record<PoolStatusEnum, number>>
> = {
  [PoolLevelEnum.PIONEERS]: {
    [PoolStatusEnum.ABOUT_TO_BLOW]: 90,
    [PoolStatusEnum.WIN_ANY_MOMENT]: 80,
    [PoolStatusEnum.IN_HIT_ZONE]: 70,
  },
  [PoolLevelEnum.LEGENDS]: {
    [PoolStatusEnum.ABOUT_TO_BLOW]: 900,
    [PoolStatusEnum.WIN_ANY_MOMENT]: 800,
    [PoolStatusEnum.IN_HIT_ZONE]: 700,
  },
  [PoolLevelEnum.TITANS]: {
    // No "About to blow" for Titans
    [PoolStatusEnum.WIN_ANY_MOMENT]: 9100,
    [PoolStatusEnum.IN_HIT_ZONE]: 7000,
  },
};

/**
 * Available bet stakes per currency.
 * Used to find the nearest valid stake after currency conversion.
 */
export const AVAILABLE_STAKES: Partial<Record<CurrencyCode, number[]>> = {
  [CurrencyCode.USD]: [
    0.1, 0.2, 0.3, 0.4, 0.5, 0.7, 1, 1.25, 2, 2.5, 3, 3.5, 5, 7.5, 10, 12.5, 15,
    20, 25, 30, 40, 50, 75,
  ],
  [CurrencyCode.EUR]: [
    0.1, 0.2, 0.3, 0.4, 0.5, 0.7, 1, 1.25, 2, 2.5, 3, 3.5, 4, 5, 7.5, 10, 12.5,
    15, 20, 25, 30, 40, 50, 75,
  ],
  [CurrencyCode.CAD]: [
    0.2, 0.25, 0.3, 0.5, 0.7, 1, 1.25, 2, 2.5, 3, 4, 5, 6, 7.5, 8, 10, 12.5, 20,
    25, 30, 35, 50, 75,
  ],
  [CurrencyCode.AUD]: [
    0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 1, 1.4, 1.6, 2, 2.4, 2.8, 3, 3.2, 3.6, 4, 5,
    6, 7.5, 8, 10, 12, 15, 16, 20, 24, 25, 28, 30, 35, 40, 50, 60, 80,
  ],
  [CurrencyCode.NOK]: [
    1, 1.1, 2, 3, 5, 6, 7, 7.5, 8, 9, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55,
    75, 100, 110, 125, 150, 200, 250, 275, 300, 500, 600, 750,
  ],
  [CurrencyCode.NZD]: [
    0.2, 0.3, 0.5, 0.7, 1, 1.5, 2, 2.5, 3, 4, 5, 6, 7, 7.5, 8, 9, 10, 12.5, 15,
    20, 25, 30, 35, 40, 45, 50, 60, 75,
  ],
};

/**
 * Finds exact match or nearest higher stake from available stakes.
 * Falls back to the converted amount if no stakes available for currency.
 */
export const findNearestStake = (
  amount: number,
  currency: CurrencyCode
): number => {
  const stakes = AVAILABLE_STAKES[currency];
  if (!stakes?.length) return amount;

  // Check for exact match first
  const exactMatch = stakes.find((stake) => stake === amount);
  if (exactMatch !== undefined) return exactMatch;

  // Find nearest higher stake
  const nearestHigher = stakes.find((stake) => stake > amount);
  if (nearestHigher !== undefined) return nearestHigher;

  // If amount exceeds all stakes, return the highest available
  return stakes[stakes.length - 1];
};

/**
 * Static pool configuration with min bet values in USD.
 * Min bets are converted to user's currency and rounded to nearest available stake.
 * Based on: 1 USD (pioneers), 5 USD (legends), 10 USD (titans)
 *
 * Supported locales → currencies:
 * - EN → USD ($)
 * - AU → AUD (A$)
 * - NZ → NZD (NZ$)
 * - CA, FR-CA → CAD (C$)
 * - FR-CH, IE, DE → EUR (€)
 * - NOK → NOK (kr)
 */
export const POOLS_CONFIG: IStaticPool[] = [
  {
    title: 'jackpot:types.pioneers',
    type: PoolLevelEnum.PIONEERS,
    image: '/jackpot/levels/pioneers.png',
    hitRange: { from: 70, to: 100 },
    minBet: 1, // USD
  },
  {
    title: 'jackpot:types.legends',
    type: PoolLevelEnum.LEGENDS,
    image: '/jackpot/levels/legends.png',
    hitRange: { from: 700, to: 1000 },
    minBet: 5, // USD
  },
  {
    title: 'jackpot:types.titans',
    type: PoolLevelEnum.TITANS,
    image: '/jackpot/levels/titans.png',
    hitRange: { from: 7000, to: 10_000 },
    minBet: 10, // USD
  },
];

// ============================================================================
// Helper Functions
// ============================================================================

const CENTS_DIVIDER = 100;

/**
 * Merges static pool config with dynamic prize data from API
 */
export const updatePoolsWithPrizes = (levels: JackpotLevel[]): IPool[] =>
  POOLS_CONFIG.reduce<IPool[]>((pools, poolConfig) => {
    const level = levels.find((lvl) => lvl.identifier === poolConfig.type);

    if (level) {
      pools.push({
        ...poolConfig,
        prize: level.amountCents / CENTS_DIVIDER,
      });
    }

    return pools;
  }, []);

/**
 * Calculates total jackpot prize sum from all levels
 */
export const getTotalSumWithCurrency = (levels: JackpotLevel[]): number =>
  levels.reduce((sum, { amountCents }) => sum + amountCents, 0) / CENTS_DIVIDER;

/**
 * Determines pool status based on current prize and pool type.
 * Returns null if prize is below all thresholds.
 */
export const getPoolStatus = (
  poolType: PoolLevelEnum,
  prize: number
): PoolStatusEnum | null => {
  const thresholds = POOL_STATUS_THRESHOLDS[poolType];

  const aboutToBlowThreshold = thresholds[PoolStatusEnum.ABOUT_TO_BLOW];
  const winAnyMomentThreshold = thresholds[PoolStatusEnum.WIN_ANY_MOMENT];
  const inHitZoneThreshold = thresholds[PoolStatusEnum.IN_HIT_ZONE];

  // Check from highest to lowest priority
  if (aboutToBlowThreshold !== undefined && prize >= aboutToBlowThreshold) {
    return PoolStatusEnum.ABOUT_TO_BLOW;
  }

  if (winAnyMomentThreshold !== undefined && prize >= winAnyMomentThreshold) {
    return PoolStatusEnum.WIN_ANY_MOMENT;
  }

  if (inHitZoneThreshold !== undefined && prize >= inHitZoneThreshold) {
    return PoolStatusEnum.IN_HIT_ZONE;
  }

  return null;
};
